import React, { ChangeEvent, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Text, Textarea } from "../controls";
import { usePersistStore, useStore } from "../store";
import DisputeTypeDropdown from "./DisputeTypeDropdown";
import { BlobService, DisputeService } from "../services";
import { useParams } from "react-router-dom";
import { PutBlobResult, put } from "@vercel/blob";
import { v4 as uuid } from "uuid";
import Utils from "../utils/utils";

interface OpenDisputeModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

const ACCEPTED_FILE_TYPES = "image/png, image/jpg, image/jpeg, application/pdf";

const formatBytes = (bytes: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const OpenDisputeModal: React.FC<OpenDisputeModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { id } = useParams();
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading, setSuccessMessage } = useStore((s: any) => s);
  const [values, setValues] = useState({
    subject: "",
    typeId: "",
    message: "",
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string>("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationMessage, setValidationMessage] = useState<string>("");

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const acceptFile = (file: File | null | undefined) => {
    if (!file) return;
    setSelectedFile(file);
    if (file.type.startsWith("image/")) {
      setFilePreview(URL.createObjectURL(file));
    } else {
      setFilePreview("");
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    acceptFile(e.dataTransfer?.files?.[0]);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: any) => {
    if (!token || !id) return;
    e.preventDefault();
    if (!values?.subject)
      return setValidationMessage("Please provide a subject.");
    if (!values?.typeId)
      return setValidationMessage("Please choose a dispute type.");
    if (!values?.message)
      return setValidationMessage(
        "Please provide a detailed message about this dispute."
      );

    setIsLoading(true);

    let fileUrl: string | null = null;
    if (selectedFile) {
      const ext = Utils.getFileExtension(selectedFile.name);
      const filename = `${uuid()}.${ext}`;
      const tokenResult = await BlobService.getClientToken(token, { filename });
      if (tokenResult.error) {
        setIsLoading(false);
        setValidationMessage(tokenResult.error);
        return;
      }
      const clientToken = tokenResult?.data?.token;
      const blob: PutBlobResult = await put(filename, selectedFile, {
        access: "public",
        token: clientToken,
      });
      if (!blob) {
        setIsLoading(false);
        setValidationMessage("Failed to upload file. Please try again.");
        return;
      }
      fileUrl = blob.url;
    }

    let result = await DisputeService.create(token, {
      adId: id,
      ...values,
      file: fileUrl,
    });
    if (result?.error) {
      setIsLoading(false);
      setValidationMessage(result?.error);
      return;
    }

    setIsLoading(false);
    handleClose();
    onSuccess?.();
    setSuccessMessage(
      "Your dispute is submitted successfully! The support team will analyse it and take proper actions."
    );
  };

  const handleClose = () => {
    setValues({ subject: "", typeId: "", message: "" });
    setSelectedFile(null);
    setFilePreview("");
    setValidationMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    onHide();
  };

  return (
    <Modal
      className="disputeModalNew"
      show={show}
      onHide={handleClose}
      aria-labelledby="open-dispute-title"
      centered
      size="lg"
    >
      <Modal.Body className="disputeModalNew__body">
        <header className="disputeModalNew__header">
          <div className="disputeModalNew__headerText">
            <span className="disputeModalNew__kicker">
              <i className="bi bi-shield-exclamation" aria-hidden="true" />
              Dispute
            </span>
            <h2 id="open-dispute-title" className="disputeModalNew__title">
              Open a dispute
            </h2>
            <p className="disputeModalNew__lead">
              Tell us what went wrong. Our support team reviews every dispute
              and will reach out within one business day.
            </p>
          </div>
          <button
            type="button"
            className="disputeModalNew__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        <div className="disputeModalNew__form">
          <Text
            name="subject"
            value={values?.subject}
            onChange={handleInputChange}
            label="Subject"
            placeholder="Briefly describe the issue"
            icon="bi-tag"
            required
          />

          <div className="disputeModalNew__field">
            <label className="field__label">
              Dispute type
              <span className="field__required" aria-hidden="true">
                *
              </span>
            </label>
            <DisputeTypeDropdown
              name="typeId"
              value={values?.typeId}
              onChange={handleInputChange}
            />
          </div>

          <Textarea
            name="message"
            value={values?.message}
            onChange={handleInputChange}
            label="Description"
            placeholder="What happened? Add as much detail as you can — dates, communications, expectations vs. reality."
            rows={5}
            required
          />

          <div className="disputeModalNew__field">
            <label className="field__label">Attachment (optional)</label>
            {!selectedFile ? (
              <div
                className={`disputeModalNew__dropzone${
                  dragging ? " disputeModalNew__dropzone--active" : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragging(false);
                }}
                onDrop={handleDrop}
              >
                <i
                  className="bi bi-cloud-arrow-up disputeModalNew__dropzoneIcon"
                  aria-hidden="true"
                />
                <div className="disputeModalNew__dropzoneTitle">
                  Drop a file here, or <span>browse</span>
                </div>
                <div className="disputeModalNew__dropzoneHint">
                  PNG, JPG, or PDF · up to 10 MB
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept={ACCEPTED_FILE_TYPES}
                  className="disputeModalNew__fileInput"
                  onChange={handleFileChange}
                />
              </div>
            ) : (
              <div className="disputeModalNew__filePreview">
                {filePreview ? (
                  <img
                    src={filePreview}
                    alt="Attachment preview"
                    className="disputeModalNew__previewImage"
                  />
                ) : (
                  <div className="disputeModalNew__previewIcon">
                    <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                  </div>
                )}
                <div className="disputeModalNew__previewInfo">
                  <div
                    className="disputeModalNew__previewName"
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </div>
                  <div className="disputeModalNew__previewMeta">
                    {formatBytes(selectedFile.size)}
                  </div>
                </div>
                <button
                  type="button"
                  className="disputeModalNew__removeBtn"
                  onClick={handleRemoveFile}
                  aria-label="Remove attachment"
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          {validationMessage && (
            <div
              className="disputeModalNew__error"
              role="alert"
            >
              <i className="bi bi-exclamation-circle" aria-hidden="true" />
              <span>{validationMessage}</span>
            </div>
          )}
        </div>

        <footer className="disputeModalNew__footer">
          <button
            type="button"
            className="disputeModalNew__cancel"
            onClick={handleClose}
          >
            Cancel
          </button>
          <Button
            title="Submit dispute"
            onClick={handleSubmit}
            className="disputeModalNew__submit"
          />
        </footer>
      </Modal.Body>
    </Modal>
  );
};

export default OpenDisputeModal;
